import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { isEmpty, isEqual, isNil } from 'lodash'
import { IconCpu, IconMinus, IconTrash } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { TextInput } from '@/components/forms/TextInput'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { Loader } from '@/components/loaders/Loader'
import { Pill } from '@/components/pills/Pill'
import Select from '@/components/forms/Select'
import {
  fetchLlmCatalog,
  fetchLlmSettings,
  fetchLlmVersions,
  resetLlmSettings,
  revertLlmSettings,
  saveLlmSettings,
} from '@/reducers/voiceAgents/voiceAgents.actions'

import ConfirmModal from '@/components/layout/modals/ConfirmModal'
import { formatVersionDate } from '../drawerHelpers'
import {
  AGENT_LEVEL_NODE_ID,
  findCatalogModel,
  formatLlmOverride,
  parseLlmVersionContent,
  sparseLlmOverride,
  temperatureAvailability,
} from '../llmHelpers'
import { OverrideHistoryList } from './OverrideEditor'
import { SettingsField } from './SettingsField'
import VersionViewingStrip from './VersionViewingStrip'

// =============================================================================
// LlmSettingsPanel — model / temperature / reasoning-effort editor for one
// target: a subagent node, or the agent-level defaults (nodeId '__agent__').
// Shares the override-editor card chrome and history/peek/revert/reset UX;
// the body is a capability-driven form instead of a textarea:
//   - model options come exactly from the BE catalog (no free text)
//   - the reasoning-effort control renders only when the effective model
//     allows efforts, with exactly the catalog's levels
//   - the temperature control follows the model's supportsTemperature flag
//     ('no' hides it; 'when_effort_none' enables it only at effort 'none')
// Controls display the *effective* value — the server's own presence-based
// per-field merge of node override → agent override → the target's own
// baseline, re-derived locally only while a draft diverges. Picking a
// value sets an override for that field; the × (only shown while the field is
// in the override) returns it to inherit. Fields the runtime will drop for
// the effective model come back in the server's `notApplied` list and get a
// "Not applied" pill.
// =============================================================================

// Everything the panel renders from: the target's own settings and history,
// plus — for a node panel — the agent-level settings the draft preview falls
// back to when the user drops a field. Shared by the mount effect and the
// error state's retry so a retry can't leave history or inheritance behind.
const loadTarget = (dispatch, agentId, nodeId, isAgentLevel) => {
  dispatch(fetchLlmSettings(agentId, nodeId))
  dispatch(fetchLlmVersions(agentId, nodeId))
  if (!isAgentLevel) {
    dispatch(fetchLlmSettings(agentId, AGENT_LEVEL_NODE_ID))
  }
}

const LlmField = ({ label, htmlFor, control, notApplied, testId }) => (
  <SettingsField
    label={label}
    htmlFor={htmlFor}
    testId={testId}
    headExtra={
      notApplied && (
        <Pill warning xs dataTestId={`${testId}-not-applied`}>
          Not applied
        </Pill>
      )
    }
  >
    {control}
  </SettingsField>
)

const LlmSettingsPanel = ({ nodeId }) => {
  const dispatch = useDispatch()
  const isAgentLevel = nodeId === AGENT_LEVEL_NODE_ID
  const testIdPrefix = isAgentLevel ? 'llm-agent' : `llm-${nodeId}`

  const agentId = useSelector((state) => state.voiceAgents.selectedAgentId)
  const catalog = useSelector((state) => state.voiceAgents.llmCatalog)
  const settings = useSelector((state) => state.voiceAgents.llmSettingsByNodeId[nodeId])
  const agentSettings = useSelector(
    (state) => state.voiceAgents.llmSettingsByNodeId[AGENT_LEVEL_NODE_ID]
  )
  const versions = useSelector((state) => state.voiceAgents.llmVersionsByNodeId[nodeId] || [])
  const catalogLoading = useSelector((state) => Boolean(state.voiceAgents.loading.llmCatalog))
  const catalogError = useSelector((state) => state.voiceAgents.error.llmCatalog)
  const settingsError = useSelector((state) => state.voiceAgents.error.llmSettings)
  const isSaving = useSelector((state) => Boolean(state.voiceAgents.loading.saveLlm))
  const isReverting = useSelector((state) => Boolean(state.voiceAgents.loading.revertLlm))
  const isResetting = useSelector((state) => Boolean(state.voiceAgents.loading.resetLlm))
  const saveError = useSelector((state) => state.voiceAgents.error.saveLlm)

  // Draft override while editing; null = mirror the stored override. The draft
  // itself is a SPARSE blob with the same presence semantics as the stored one
  // (absent = inherit, explicit null = cleared) — the PATCH body is built from
  // its present fields, so an untouched field is never sent.
  const [draft, setDraft] = useState(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [peekVersion, setPeekVersion] = useState(null)
  const [revertTarget, setRevertTarget] = useState(null)
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false)

  useEffect(() => {
    if (!catalog.loaded && !catalogLoading && !catalogError) {
      dispatch(fetchLlmCatalog())
    }
  }, [dispatch, catalog.loaded, catalogLoading, catalogError])

  // Refetch per target so the panel always reflects the latest agent-level
  // cascade. A node panel also loads the agent-level settings, but only to
  // preview what a field falls back to once the user drops it from the draft —
  // the saved state renders from the server's own `effective`.
  useEffect(() => {
    loadTarget(dispatch, agentId, nodeId, isAgentLevel)
    setDraft(null)
    setHistoryOpen(false)
    setPeekVersion(null)
  }, [dispatch, agentId, nodeId, isAgentLevel])

  const { models } = catalog
  const storedOverride = sparseLlmOverride(settings?.override)
  const dirty = Boolean(draft) && !isEqual(draft, storedOverride)

  const ordered = [...versions].sort((a, b) => (b.version || 0) - (a.version || 0))
  const latestVersionNumber = ordered[0]?.version
  const peekEntry = !isNil(peekVersion)
    ? ordered.find((entry) => entry.version === peekVersion)
    : null
  const isViewing = Boolean(peekEntry) && peekEntry.version !== latestVersionNumber
  const versionCount = ordered.length

  // The override the form renders — a SPARSE blob (absent = inherit, null =
  // cleared): while peeking an older version, that version's blob (read-only,
  // {} for a reset entry); otherwise the in-progress draft or the stored one.
  const override = isViewing
    ? sparseLlmOverride(parseLlmVersionContent(peekEntry.content))
    : draft ?? storedOverride

  // The layer below this target's own override: the agent-level override, then
  // the target's OWN compiled baseline. Mirrors the BE merge order
  // (llm_settings.py effective_llm_settings) — each layer presence-based, and
  // a null in a layer is "cleared" (terminates the chain).
  const belowTargetValue = (field) => {
    if (!isAgentLevel && agentSettings?.override && field in agentSettings.override) {
      return agentSettings.override[field]
    }
    return settings?.baseline?.[field] ?? null
  }

  // What a field falls back to when the override omits it. For the saved state
  // the server already resolved the whole chain and sent it as `effective` —
  // render that instead of re-deriving it, so the panel can't drift from the
  // runtime and doesn't paint node baselines while the agent-level fetch is
  // still in flight (or after it failed). Only a diverging draft or a peeked
  // version needs the local merge.
  const inheritedValue = (field) => {
    if (isViewing || dirty) {
      return belowTargetValue(field)
    }
    return settings?.effective?.[field] ?? null
  }

  // The merge the runtime would use if the draft were saved — capability
  // gating follows this, not the stored state, so controls react while editing.
  const effectiveValue = (field) => (field in override ? override[field] : inheritedValue(field))
  const effective = {
    model: effectiveValue('model'),
    temperature: effectiveValue('temperature'),
    reasoningEffort: effectiveValue('reasoningEffort'),
  }
  const modelEntry = findCatalogModel(models, effective.model)
  const tempAvailability = temperatureAvailability(modelEntry, effective.reasoningEffort)
  // Server-computed list of effective fields the runtime will drop for the
  // effective model ("inherited, not applied"). Only meaningful for the saved
  // state — hidden while a draft diverges or an old version is peeked.
  const notAppliedFields = !isViewing && !dirty ? settings?.notApplied || [] : []
  // Row summaries render the parsed override, not the raw JSON blob.
  const historyEntries = ordered.map((entry) => ({
    ...entry,
    content: formatLlmOverride(parseLlmVersionContent(entry.content), models),
  }))
  // Node panels surface Reset whenever an override exists (dropping it falls
  // back to the agent settings / the node's baseline); the agent-level panel
  // keeps it tucked behind the history view like the other override editors.
  const showResetRow =
    Boolean(settings?.hasOverride) && !isViewing && (!isAgentLevel || historyOpen)

  const setField = (field, value) => setDraft({ ...override, [field]: value })
  // Back to "inherit": remove the key entirely (a null value would mean
  // "cleared" — suppress inheritance — which is a different state).
  const dropField = (field) => {
    const next = { ...override }
    delete next[field]
    setDraft(next)
  }

  // Re-gate params the user explicitly SET when the effective model changes:
  // a set effort the new model doesn't allow, or a set temperature it can't
  // take, falls back to inherit in the draft (visible before saving). Cleared
  // (null) fields stay — they're legal for every model. Inherited values need
  // no gating: the BE's provenance-aware validation never rejects them (the
  // runtime just drops them, reported via notApplied).
  const regateParams = (next, nextEntry) => {
    if (
      !isNil(next.reasoningEffort) &&
      !nextEntry?.allowedEfforts?.includes(next.reasoningEffort)
    ) {
      delete next.reasoningEffort
    }
    const nextEffort =
      'reasoningEffort' in next ? next.reasoningEffort : inheritedValue('reasoningEffort')
    if (!isNil(next.temperature) && !temperatureAvailability(nextEntry, nextEffort).enabled) {
      delete next.temperature
    }
  }

  const handleModelChange = (option) => {
    const next = { ...override }
    if (option?.value) {
      next.model = option.value
    } else {
      delete next.model
    }
    const nextEntry = findCatalogModel(
      models,
      'model' in next ? next.model : inheritedValue('model')
    )
    regateParams(next, nextEntry)
    setDraft(next)
  }

  const handleEffortChange = (option) => {
    const next = { ...override }
    if (option?.value) {
      next.reasoningEffort = option.value
    } else {
      delete next.reasoningEffort
    }
    regateParams(next, modelEntry)
    setDraft(next)
  }

  const handleSave = async () => {
    // An empty override means "inherit everything" — that's the BE's reset
    // (DELETE, which writes a version) rather than a PATCH (422 on no fields).
    const result = isEmpty(override)
      ? await dispatch(resetLlmSettings(agentId, nodeId))
      : await dispatch(saveLlmSettings(agentId, nodeId, override))
    if (result) {
      setDraft(null)
      setPeekVersion(null)
    }
  }

  const confirmRevert = async () => {
    if (!revertTarget) {
      return
    }
    const result = await dispatch(revertLlmSettings(agentId, nodeId, revertTarget.version))
    if (result) {
      setRevertTarget(null)
      setPeekVersion(null)
      setDraft(null)
    }
  }

  const confirmReset = async () => {
    // The thunks all resolve falsy only on failure, so the form state is
    // cleared on the same condition everywhere.
    const result = await dispatch(resetLlmSettings(agentId, nodeId))
    if (result) {
      setResetConfirmOpen(false)
      setPeekVersion(null)
      setDraft(null)
    }
  }

  const handlePeek = (versionNumber) => {
    if (versionNumber === latestVersionNumber) {
      setPeekVersion(null)
      return
    }
    setPeekVersion(peekVersion === versionNumber ? null : versionNumber)
  }

  const handleToggleHistory = () => {
    if (historyOpen) {
      setPeekVersion(null)
    }
    setHistoryOpen(!historyOpen)
  }

  const loadError = catalogError || (!settings && settingsError)
  if (loadError) {
    return (
      <div
        className={classNames('voice-agents__llm-panel', {
          'voice-agents__llm-panel--card': isAgentLevel,
        })}
        data-testid={`${testIdPrefix}-editor`}
      >
        <ErrorMessage content="Couldn't load LLM settings." />
        <Button
          small
          onClick={() => {
            dispatch(fetchLlmCatalog())
            loadTarget(dispatch, agentId, nodeId, isAgentLevel)
          }}
        >
          Try again
        </Button>
      </div>
    )
  }
  if (!settings || !catalog.loaded) {
    return <Loader centered label="Loading LLM settings…" />
  }

  const modelOptions = models.map((entry) => ({ value: entry.id, label: entry.label }))
  const effortOptions = (modelEntry?.allowedEfforts || []).map((effort) => ({
    value: effort,
    label: effort,
  }))

  return (
    <div
      className={classNames('voice-agents__override-editor', 'voice-agents__llm-panel', {
        'voice-agents__llm-panel--card': isAgentLevel,
      })}
      data-testid={`${testIdPrefix}-editor`}
    >
      <div className="voice-agents__override-editor-head">
        <div className="voice-agents__override-editor-title">
          <span className="voice-agents__override-editor-avatar">
            <IconCpu size={14} />
          </span>
          <span className="voice-agents__override-editor-label">LLM settings</span>
        </div>
        {versionCount > 0 ? (
          <button
            type="button"
            className="voice-agents__override-editor-edits-toggle"
            onClick={handleToggleHistory}
            data-testid={`${testIdPrefix}-history-toggle`}
          >
            {historyOpen ? (
              <>
                <IconMinus size={14} /> Hide edits
              </>
            ) : (
              `All edits (${versionCount})`
            )}
          </button>
        ) : (
          <span className="voice-agents__override-editor-edits-toggle voice-agents__override-editor-edits-toggle--empty">
            All edits (0)
          </span>
        )}
      </div>

      {/* The same form renders while peeking an older version — populated
          with that version's values and fully disabled, so history reads
          exactly like the live state. */}
      <div className="voice-agents__llm-form">
        <LlmField
          label="Model"
          htmlFor={`${testIdPrefix}-model`}
          testId={`${testIdPrefix}-model`}
          control={
            <Select
              inputId={`${testIdPrefix}-model`}
              options={modelOptions}
              value={effective.model}
              onChange={handleModelChange}
              isClearable={!isViewing && 'model' in override}
              isDisabled={isSaving || isViewing}
            />
          }
        />

        {/* A field whose control the model doesn't offer still renders (as a
            static value) when the server flags it notApplied — it must show
            as "not applied", not silently disappear. */}
        {(!isEmpty(modelEntry?.allowedEfforts) || notAppliedFields.includes('reasoningEffort')) && (
          <LlmField
            label="Reasoning effort"
            htmlFor={`${testIdPrefix}-effort`}
            testId={`${testIdPrefix}-effort`}
            notApplied={notAppliedFields.includes('reasoningEffort')}
            control={
              isEmpty(modelEntry?.allowedEfforts) ? (
                <div className="voice-agents__llm-static-value">{effective.reasoningEffort}</div>
              ) : (
                <Select
                  inputId={`${testIdPrefix}-effort`}
                  options={effortOptions}
                  value={effective.reasoningEffort}
                  onChange={handleEffortChange}
                  isClearable={!isViewing && 'reasoningEffort' in override}
                  isDisabled={isSaving || isViewing}
                />
              )
            }
          />
        )}

        {(tempAvailability.visible || notAppliedFields.includes('temperature')) && (
          <LlmField
            label="Temperature"
            htmlFor={`${testIdPrefix}-temperature`}
            testId={`${testIdPrefix}-temperature`}
            notApplied={notAppliedFields.includes('temperature')}
            control={
              !tempAvailability.visible ? (
                <div className="voice-agents__llm-static-value">{effective.temperature}</div>
              ) : (
                <TextInput
                  id={`${testIdPrefix}-temperature`}
                  type="number"
                  step="0.1"
                  min="0"
                  max={modelEntry?.temperatureMax ?? 2}
                  value={effective.temperature ?? ''}
                  disabled={isSaving || isViewing || !tempAvailability.enabled}
                  onChange={(event) =>
                    event.target.value === ''
                      ? dropField('temperature')
                      : setField('temperature', Number(event.target.value))
                  }
                />
              )
            }
          />
        )}

        {modelEntry?.notes && <p className="voice-agents__llm-notes">{modelEntry.notes}</p>}

        {/* Gate on dirty so the shared error slot never leaks onto the other
            panel (agent-level vs node) after a target switch. */}
        {!isViewing && dirty && saveError && <ErrorMessage content={saveError} />}

        {!isViewing && dirty && (
          <div className="voice-agents__llm-actions">
            <Button
              small
              disabled={isSaving}
              onClick={() => setDraft(null)}
              data-testid={`${testIdPrefix}-cancel`}
            >
              Cancel
            </Button>
            <Button
              brand
              small
              loading={isSaving}
              disabled={isSaving}
              onClick={handleSave}
              data-testid={`${testIdPrefix}-save`}
            >
              Save
            </Button>
          </div>
        )}
      </div>

      {isViewing && (
        <VersionViewingStrip
          testIdPrefix={testIdPrefix}
          version={peekEntry}
          busy={isReverting}
          onRevert={() => setRevertTarget(peekEntry)}
        />
      )}

      {historyOpen && versionCount > 0 && (
        <OverrideHistoryList
          testIdPrefix={testIdPrefix}
          ordered={historyEntries}
          latestVersionNumber={latestVersionNumber}
          peekVersion={peekVersion}
          onPeek={handlePeek}
        />
      )}

      {showResetRow && (
        <div className="voice-agents__override-editor-reset-row">
          <Button
            basic
            small
            critical
            onClick={() => setResetConfirmOpen(true)}
            disabled={isResetting}
            data-testid={`${testIdPrefix}-reset`}
            title="Drop the override and fall back to the seeded baseline"
          >
            <IconTrash /> Reset to baseline
          </Button>
        </div>
      )}

      <ConfirmModal
        show={Boolean(revertTarget)}
        onClose={() => setRevertTarget(null)}
        title={
          revertTarget
            ? `Revert LLM settings to ${formatVersionDate(revertTarget.createdAt)}?`
            : 'Revert'
        }
        confirmLabel="Revert"
        busyLabel="Reverting…"
        busy={isReverting}
        confirmDisabled={!revertTarget}
        onConfirm={confirmRevert}
        testIdPrefix={`${testIdPrefix}-revert`}
      >
        {revertTarget && (
          <p>
            This creates a <strong>new edit</strong> with the settings from{' '}
            <strong>{formatVersionDate(revertTarget.createdAt)}</strong>. The current settings stay
            in history.
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        show={resetConfirmOpen}
        onClose={() => setResetConfirmOpen(false)}
        title="Reset LLM settings to baseline?"
        confirmLabel="Reset"
        busyLabel="Resetting…"
        busy={isResetting}
        destructive
        onConfirm={confirmReset}
        testIdPrefix={`${testIdPrefix}-reset`}
      >
        <p>
          Drops the override so every field inherits again. History is kept — this writes a new
          “reset” edit.
        </p>
      </ConfirmModal>
    </div>
  )
}

export default LlmSettingsPanel
